import React, { useEffect, useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CheckSquare, Plus, Trash2, MessageSquare, Calendar, Search, Archive, ArchiveRestore, Wrench, Gauge, Paperclip, X, Send, Pencil } from "lucide-react";
import { roleLabel } from "@/lib/permissions";
import { RepairReviewDialog } from "@/components/RepairReviewDialog";
import { KpiReviewDialog } from "@/components/KpiReviewDialog";
import { EmployeeSearchSelect } from "@/components/EmployeeSearchSelect";
import { toast } from "sonner";

const STATUSES = ["Pending", "Started", "Complete", "Reject"];

const statusStyle = (s) => ({
  Pending: "bg-gray-100 text-gray-700",
  Started: "bg-sky-100 text-sky-800",
  Complete: "bg-emerald-100 text-emerald-800",
  Reject: "bg-rose-100 text-rose-800",
}[s] || "bg-gray-100 text-gray-700");

const TaskCard = ({ task, viewer, onStatus, onDelete, onArchive, onUnarchive, onRepairReview, onKpiReview, onPostComment, onAttach, onDownloadAttach, onDeleteAttach, onEdit }) => {
  const isAssigner = task.assigner_id === viewer.id;
  const isAssignee = task.assignee_id === viewer.id;
  const isAdmin = viewer.role === "admin";
  const isArchived = !!task.archived_at;
  const canArchive = (isAssigner || isAdmin) && task.status === "Complete" && !isArchived;
  const canUnarchive = (isAssigner || isAdmin) && isArchived;
  const canInteract = isAssigner || isAssignee || isAdmin;
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const thread = [
    ...(task.assignee_comment
      ? [{ id: "legacy", by_name: task.assignee_name, text: task.assignee_comment, at: task.assignee_comment_at }]
      : []),
    ...(task.comments || []),
  ];
  const post = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    const ok = await onPostComment(task, newComment.trim());
    if (ok) setNewComment("");
    setPosting(false);
  };
  return (
    <div className={`border border-summit-hairline rounded-lg bg-white p-5 ${isArchived ? "opacity-70" : ""}`} data-testid={`task-card-${task.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-base font-medium tracking-tight truncate" data-testid={`task-title-${task.id}`}>{task.title}</h3>
            <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${statusStyle(task.status)}`} data-testid={`task-status-${task.id}`}>
              {task.status}
            </span>
            {isArchived && (
              <span className="px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-gray-900 text-white" data-testid={`task-archived-badge-${task.id}`}>
                Archived
              </span>
            )}
          </div>
          <div className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-500 flex flex-wrap gap-x-3 gap-y-1">
            <span>Assigner: {task.assigner_name}</span>
            <span>Assignee: {task.assignee_name}</span>
            {task.due_date && (
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Due {task.due_date}</span>
            )}
          </div>
          {task.description && (
            <p className="mt-3 text-sm text-gray-700 whitespace-pre-wrap">{task.description}</p>
          )}
          {task.repair_request_id && (
            <div className="mt-2.5 flex items-center gap-3 flex-wrap">
              {isAssignee && task.status !== "Complete" && (
                <Button size="sm" className="h-8 bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => onRepairReview(task)} data-testid={`task-repair-review-${task.id}`}>
                  <Wrench className="w-3.5 h-3.5 mr-1.5" /> Review &amp; decide
                </Button>
              )}
              {isAssigner && !isArchived && (
                <Link to="/repair-approvals" className="text-xs font-medium text-summit-primary underline underline-offset-2" data-testid={`task-repair-link-${task.id}`}>
                  Open in Repair Approvals →
                </Link>
              )}
            </div>
          )}
          {task.kpi_id && (
            <div className="mt-2.5 flex items-center gap-3 flex-wrap">
              {isAssignee && task.status !== "Complete" && (
                <Button size="sm" className="h-8 bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => onKpiReview(task)} data-testid={`task-kpi-review-${task.id}`}>
                  <Gauge className="w-3.5 h-3.5 mr-1.5" /> Review KPI
                </Button>
              )}
              {isAssignee && task.status === "Complete" && (
                <Button size="sm" variant="outline" className="h-8" onClick={() => onKpiReview(task)} data-testid={`task-kpi-view-${task.id}`}>
                  <Gauge className="w-3.5 h-3.5 mr-1.5" /> View KPI
                </Button>
              )}
              {isAssigner && !isArchived && (
                <Link to="/team/kpi" className="text-xs font-medium text-summit-primary underline underline-offset-2" data-testid={`task-kpi-link-${task.id}`}>
                  Open in KPI Tracking →
                </Link>
              )}
            </div>
          )}
          {task.maint_wo_id && (
            <div className="mt-2.5 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-sky-100 text-sky-800" data-testid={`task-maint-chip-${task.id}`}>
                <Wrench className="w-3 h-3" /> Maintenance · {task.maint_tail}
              </span>
              <Link to="/fleet/maintenance" className="text-xs font-medium text-summit-primary underline underline-offset-2" data-testid={`task-maint-link-${task.id}`}>
                Open in Maintenance →
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-summit-hairline">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-500">
          <Paperclip className="w-3 h-3" /> Attachments
        </div>
        {(task.attachments || []).length > 0 ? (
          <div className="mt-1.5 space-y-1" data-testid={`task-attachments-${task.id}`}>
            {(task.attachments || []).map((a) => (
              <div key={a.id} className="flex items-center gap-2 text-xs text-gray-600">
                <Paperclip className="w-3 h-3 shrink-0 text-gray-400" />
                <button type="button" className="truncate font-medium hover:underline" onClick={() => onDownloadAttach(task, a)} data-testid={`task-attach-dl-${a.id}`}>{a.filename}</button>
                <span className="text-gray-400">{a.size < 1024 ? `${a.size} B` : `${Math.round(a.size / 1024)} KB`} · {a.uploaded_by_name}</span>
                {(isAdmin || viewer.id === a.uploaded_by || isAssigner) && !isArchived && (
                  <button type="button" className="p-0.5 text-gray-400 hover:text-rose-600" onClick={() => onDeleteAttach(task, a)} data-testid={`task-attach-del-${a.id}`}>
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-1.5 text-xs text-gray-400 italic">No files attached.</p>
        )}
        {canInteract && !isArchived && (
          <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => onAttach(task)} data-testid={`task-attach-btn-${task.id}`}>
            <Paperclip className="w-3 h-3 mr-1.5" /> Attach file
          </Button>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-summit-hairline">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-500">
          <MessageSquare className="w-3 h-3" /> Comments
        </div>
        {thread.length === 0 ? (
          <p className="mt-1.5 text-sm text-gray-400 italic">No comments yet.</p>
        ) : (
          <div className="mt-2 space-y-2" data-testid={`task-comments-${task.id}`}>
            {thread.map((c) => (
              <div key={c.id} className={`rounded-md px-3 py-2 border ${c.by_name === task.assigner_name ? "bg-sky-50/60 border-sky-100" : "bg-gray-50 border-summit-hairline"}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  {c.by_name}{c.at ? ` · ${new Date(c.at).toLocaleString()}` : ""}
                </div>
                <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap">{c.text}</p>
              </div>
            ))}
          </div>
        )}
        {canInteract && !isArchived && (
          <div className="mt-2 flex items-start gap-2">
            <Textarea
              rows={1}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment…"
              className="min-h-[38px] text-sm"
              data-testid={`task-comment-input-${task.id}`}
            />
            <Button size="sm" className="h-9 bg-summit-primary hover:bg-summit-primary-soft text-white shrink-0" onClick={post} disabled={posting || !newComment.trim()} data-testid={`task-comment-post-${task.id}`}>
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {(isAssigner || isAssignee || isAdmin) && (
          <Select value={task.status} onValueChange={(v) => onStatus(task, v)}>
            <SelectTrigger className="w-[160px] h-9" data-testid={`change-status-${task.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`status-option-${task.id}-${s}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {(isAssigner || isAdmin) && !isArchived && (
          <Button size="sm" variant="outline" onClick={() => onEdit(task)} data-testid={`edit-task-${task.id}`}>
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
          </Button>
        )}
        {canArchive && (
          <Button size="sm" variant="outline" onClick={() => onArchive(task)} data-testid={`archive-task-${task.id}`}>
            <Archive className="w-3.5 h-3.5 mr-1.5" /> Archive
          </Button>
        )}
        {canUnarchive && (
          <Button size="sm" variant="outline" onClick={() => onUnarchive(task)} data-testid={`unarchive-task-${task.id}`}>
            <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" /> Unarchive
          </Button>
        )}
        {(isAssigner || isAdmin) && (
          <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50" onClick={() => onDelete(task)} data-testid={`delete-task-${task.id}`}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
          </Button>
        )}
      </div>
    </div>
  );
};

export default function Tasks() {
  const { user } = useAuth();
  const [tab, setTab] = useState("assigned");
  const [assigned, setAssigned] = useState([]);
  const [created, setCreated] = useState([]);
  const [archived, setArchived] = useState([]);
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState("");
  const [createModal, setCreateModal] = useState({ open: false, assignee_ids: [""], title: "", description: "", due_date: "" });
  const [repairReq, setRepairReq] = useState(null);
  const [kpiRec, setKpiRec] = useState(null);
  const [maintDone, setMaintDone] = useState(null); // {task, labor_hours, notes, parts}
  const [editModal, setEditModal] = useState(null); // {id, title, description, due_date}

  const openEdit = (t) => setEditModal({ id: t.id, title: t.title, description: t.description || "", due_date: t.due_date || "" });

  const submitEdit = async () => {
    if (!editModal.title.trim()) { toast.error("Title required"); return; }
    try {
      await api.patch(`/tasks/${editModal.id}`, {
        title: editModal.title,
        description: editModal.description,
        due_date: editModal.due_date || "",
      });
      toast.success("Task updated");
      setEditModal(null);
      await load(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update task"); }
  };

  const openKpiReview = async (task) => {
    try {
      const r = await api.get(`/kpi/${task.kpi_id}`);
      setKpiRec(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load KPI");
    }
  };

  const openRepairReview = async (task) => {
    try {
      const r = await api.get(`/repair-approvals/${task.repair_request_id}`);
      setRepairReq(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load repair request");
    }
  };

  const load = async (q = "") => {
    try {
      const qs = q ? `&q=${encodeURIComponent(q)}` : "";
      const [r1, r3] = await Promise.all([
        api.get(`/tasks?filter=assigned${qs}`),
        api.get(`/tasks?filter=archived${qs}`),
      ]);
      setAssigned(r1.data);
      setArchived(r3.data);
      if (user.can_have_reports) {
        const [r2, r4] = await Promise.all([
          api.get(`/tasks?filter=created${qs}`),
          api.get("/direct-reports"),
        ]);
        setCreated(r2.data);
        setReports(r4.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tasks");
    }
  };
  useEffect(() => { load(search); }, [search]);

  const onStatus = async (task, status) => {
    if (status === task.status) return;
    if (status === "Complete" && task.maint_wo_id) {
      setMaintDone({ task, labor_hours: "", notes: "", parts: "" });
      return;
    }
    try {
      await api.patch(`/tasks/${task.id}/status`, { status });
      toast.success(`Marked ${status}`);
      await load(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const completeMaintTask = async () => {
    if (maintDone.labor_hours === "" || Number(maintDone.labor_hours) <= 0) { toast.error("Allocated hours are required"); return; }
    try {
      await api.post(`/maintenance/work-orders/${maintDone.task.maint_wo_id}/complete`, {
        notes: maintDone.notes,
        parts: maintDone.parts,
        labor_hours: Number(maintDone.labor_hours),
      });
      toast.success("Maintenance job completed — logged on the aircraft");
      setMaintDone(null);
      await load(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to complete"); }
  };

  const postComment = async (task, text) => {
    try {
      await api.post(`/tasks/${task.id}/comments`, { text });
      await load(search);
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to post comment"); return false; }
  };

  const fileRef = React.useRef(null);
  const uploadTaskRef = React.useRef(null);
  const startAttach = (task) => { uploadTaskRef.current = task.id; fileRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const tid = uploadTaskRef.current;
    if (!file || !tid) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/tasks/${tid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment added");
      await load(search);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };
  const downloadAttach = async (task, a) => {
    try {
      const resp = await fetch(`${API_BASE}/tasks/${task.id}/attachments/${a.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const deleteAttach = async (task, a) => {
    try {
      await api.delete(`/tasks/${task.id}/attachments/${a.id}`);
      toast.success("Attachment removed");
      await load(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  const onDelete = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task deleted");
      await load(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onArchive = async (task) => {
    try {
      await api.patch(`/tasks/${task.id}/archive`);
      toast.success("Archived");
      await load(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onUnarchive = async (task) => {
    try {
      await api.patch(`/tasks/${task.id}/unarchive`);
      toast.success("Unarchived");
      await load(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitCreate = async () => {
    const ids = [...new Set(createModal.assignee_ids.filter(Boolean))];
    if (ids.length === 0) { toast.error("Pick an assignee"); return; }
    if (!createModal.title.trim()) { toast.error("Title required"); return; }
    try {
      await api.post("/tasks", {
        assignee_ids: ids,
        title: createModal.title,
        description: createModal.description,
        due_date: createModal.due_date || null,
      });
      toast.success(ids.length > 1 ? `Task assigned to ${ids.length} people` : "Task assigned");
      setCreateModal({ open: false, assignee_ids: [""], title: "", description: "", due_date: "" });
      setTab("created");
      await load(search);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="task-file-input" />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
            <CheckSquare className="w-3 h-3" /> Workflow
          </div>
          <h1 className="font-display text-3xl font-medium tracking-tight mt-1" data-testid="tasks-heading">Tasks</h1>
          <p className="text-sm text-gray-500 mt-1">
            {user.can_have_reports
              ? "Assign work to your direct reports and track their status."
              : "Tasks assigned to you by your manager."}
          </p>
        </div>
        {user.can_have_reports && (
          <Button
            className="bg-summit-primary hover:bg-summit-primary-soft text-white"
            onClick={() => setCreateModal({ open: true, assignee_ids: [""], title: "", description: "", due_date: "" })}
            data-testid="new-task-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New task
          </Button>
        )}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks by title, description, or name…"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-summit-hairline text-sm bg-white"
            data-testid="task-search-input"
          />
        </div>
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="font-mono text-[10px] uppercase tracking-widest text-gray-500 hover:text-gray-900"
            data-testid="task-search-clear"
          >Clear</button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="assigned" data-testid="tab-assigned">Assigned to me ({assigned.length})</TabsTrigger>
          {user.can_have_reports && (
            <TabsTrigger value="created" data-testid="tab-created">I assigned ({created.length})</TabsTrigger>
          )}
          <TabsTrigger value="archived" data-testid="tab-archived">Archived ({archived.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="assigned" className="mt-4 space-y-3">
          {assigned.length === 0 && (
            <div className="border border-dashed border-summit-hairline rounded-lg p-8 text-center text-sm text-gray-400" data-testid="empty-assigned">
              {search ? "No tasks match your search." : "No tasks assigned to you."}
            </div>
          )}
          {assigned.map((t) => (
            <TaskCard key={t.id} task={t} viewer={user} onStatus={onStatus} onDelete={onDelete} onArchive={onArchive} onUnarchive={onUnarchive} onRepairReview={openRepairReview} onKpiReview={openKpiReview} onPostComment={postComment} onAttach={startAttach} onDownloadAttach={downloadAttach} onDeleteAttach={deleteAttach} onEdit={openEdit} />
          ))}
        </TabsContent>

        {user.can_have_reports && (
          <TabsContent value="created" className="mt-4 space-y-3">
            {created.length === 0 && (
              <div className="border border-dashed border-summit-hairline rounded-lg p-8 text-center text-sm text-gray-400" data-testid="empty-created">
                {search ? "No tasks match your search." : "You haven't assigned any tasks yet."}
              </div>
            )}
            {created.map((t) => (
              <TaskCard key={t.id} task={t} viewer={user} onStatus={onStatus} onDelete={onDelete} onArchive={onArchive} onUnarchive={onUnarchive} onRepairReview={openRepairReview} onKpiReview={openKpiReview} onPostComment={postComment} onAttach={startAttach} onDownloadAttach={downloadAttach} onDeleteAttach={deleteAttach} onEdit={openEdit} />
            ))}
          </TabsContent>
        )}

        <TabsContent value="archived" className="mt-4 space-y-3">
          {archived.length === 0 && (
            <div className="border border-dashed border-summit-hairline rounded-lg p-8 text-center text-sm text-gray-400" data-testid="empty-archived">
              {search ? "No archived tasks match your search." : "Nothing archived yet."}
            </div>
          )}
          {archived.map((t) => (
            <TaskCard key={t.id} task={t} viewer={user} onStatus={onStatus} onDelete={onDelete} onArchive={onArchive} onUnarchive={onUnarchive} onRepairReview={openRepairReview} onKpiReview={openKpiReview} onPostComment={postComment} onAttach={startAttach} onDownloadAttach={downloadAttach} onDeleteAttach={deleteAttach} onEdit={openEdit} />
          ))}
        </TabsContent>
      </Tabs>

      {/* Create task modal */}
      <Dialog open={createModal.open} onOpenChange={(o) => !o && setCreateModal({ open: false, assignee_ids: [""], title: "", description: "", due_date: "" })}>
        <DialogContent className="max-w-lg" data-testid="create-task-modal">
          <DialogHeader>
            <DialogTitle>Assign a new task</DialogTitle>
            <DialogDescription>Task will be created with status &quot;Pending&quot;. Both you and the assignee can update the status.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Assignees</Label>
              <div className="mt-1.5 space-y-2">
                {createModal.assignee_ids.map((aid, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <EmployeeSearchSelect
                        options={reports
                          .filter((r) => r.id === aid || !createModal.assignee_ids.includes(r.id))
                          .map((r) => ({ id: r.id, label: `${r.last_name}, ${r.first_initial}`, sublabel: r.job_title || roleLabel(r.role) }))}
                        value={aid}
                        onChange={(v) => setCreateModal((m) => ({ ...m, assignee_ids: m.assignee_ids.map((x, j) => (j === i ? v : x)) }))}
                        triggerPlaceholder={reports.length === 0 ? "No direct reports yet" : "Search & pick a direct report"}
                        testId={`task-assignee-select-${i}`}
                      />
                    </div>
                    {createModal.assignee_ids.length > 1 && (
                      <button
                        type="button"
                        className="text-gray-300 hover:text-rose-500 shrink-0"
                        onClick={() => setCreateModal((m) => ({ ...m, assignee_ids: m.assignee_ids.filter((_, j) => j !== i) }))}
                        data-testid={`task-assignee-remove-${i}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-summit-primary hover:underline disabled:opacity-40"
                  onClick={() => setCreateModal((m) => ({ ...m, assignee_ids: [...m.assignee_ids, ""] }))}
                  disabled={createModal.assignee_ids.length >= reports.length}
                  data-testid="task-assignee-add"
                >
                  <Plus className="w-3 h-3" /> Add another assignee
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Title</Label>
              <Input
                value={createModal.title}
                onChange={(e) => setCreateModal((m) => ({ ...m, title: e.target.value }))}
                placeholder="e.g. Complete brake inspection on C-FLRJ"
                data-testid="task-title-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Description</Label>
              <Textarea
                value={createModal.description}
                onChange={(e) => setCreateModal((m) => ({ ...m, description: e.target.value }))}
                rows={3}
                placeholder="Details, deliverables, references…"
                data-testid="task-description-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Due date (optional)</Label>
              <Input
                type="date"
                value={createModal.due_date}
                onChange={(e) => setCreateModal((m) => ({ ...m, due_date: e.target.value }))}
                data-testid="task-due-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModal({ open: false, assignee_ids: [""], title: "", description: "", due_date: "" })}>Cancel</Button>
            <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitCreate} data-testid="task-create-submit">
              Create task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit task modal (assigner/admin) */}
      <Dialog open={!!editModal} onOpenChange={(o) => !o && setEditModal(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-task-modal">
          <DialogHeader>
            <DialogTitle>Edit task</DialogTitle>
            <DialogDescription>Add or remove information — the assignee keeps their status and comments.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Title</Label>
              <Input
                value={editModal?.title || ""}
                onChange={(e) => setEditModal((m) => ({ ...m, title: e.target.value }))}
                data-testid="task-edit-title-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Description</Label>
              <Textarea
                value={editModal?.description || ""}
                onChange={(e) => setEditModal((m) => ({ ...m, description: e.target.value }))}
                rows={4}
                placeholder="Details, deliverables, references…"
                data-testid="task-edit-description-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Due date (clear to remove)</Label>
              <Input
                type="date"
                value={editModal?.due_date || ""}
                onChange={(e) => setEditModal((m) => ({ ...m, due_date: e.target.value }))}
                data-testid="task-edit-due-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal(null)}>Cancel</Button>
            <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitEdit} data-testid="task-edit-submit">
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance-linked task completion — completes the aircraft job properly */}
      <Dialog open={!!maintDone} onOpenChange={(o) => { if (!o) setMaintDone(null); }}>
        <DialogContent className="max-w-md" data-testid="task-maint-complete-form">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Wrench className="w-4 h-4 text-emerald-600" /> Complete maintenance job</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap">{maintDone?.task?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Hours allocated *</Label>
              <Input className="mt-1" type="number" min="0.1" step="0.1" placeholder="e.g. 2.5" value={maintDone?.labor_hours || ""} onChange={(e) => setMaintDone((m) => ({ ...m, labor_hours: e.target.value }))} data-testid="task-maint-done-hours" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Work performed / notes</Label>
              <Textarea className="mt-1 text-sm" rows={3} placeholder="What was done?" value={maintDone?.notes || ""} onChange={(e) => setMaintDone((m) => ({ ...m, notes: e.target.value }))} data-testid="task-maint-done-notes" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Parts used (optional)</Label>
              <Input className="mt-1" value={maintDone?.parts || ""} onChange={(e) => setMaintDone((m) => ({ ...m, parts: e.target.value }))} data-testid="task-maint-done-parts" />
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={completeMaintTask} data-testid="task-maint-done-confirm">Complete job &amp; task</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Repair approval review — alert-style modal straight from the task */}
      {repairReq && (
        <RepairReviewDialog
          req={repairReq}
          canDecide={repairReq.approver_id === user.id || user.role === "admin"}
          onOpenChange={() => setRepairReq(null)}
          onDecided={() => { setRepairReq(null); load(search); }}
        />
      )}

      {/* KPI scorecard review — acknowledge or request a correction from the task */}
      {kpiRec && (
        <KpiReviewDialog
          kpi={kpiRec}
          canRespond={kpiRec.employee_id === user.id && kpiRec.status === "pushed"}
          onOpenChange={() => setKpiRec(null)}
          onDone={() => { setKpiRec(null); load(search); }}
        />
      )}
    </div>
  );
}
